import React from "react";
import { ProtectContext } from "../../context/protect_context";
import { setPageTitle } from "../../utils/page_title";
import type { Page, SectionWithData } from "../../types";
import { isSectionWithData } from "../../types";
import { Link } from "react-router-dom";

const LocationPage: React.FC = () => {
  const { data } = React.useContext(ProtectContext);

  // Find location page by href
  const findPageByHref = (pages: Page[], href: string): Page | null => {
    for (const page of pages) {
      if (page.href === href || page.href === `/${href}`) {
        return page;
      }
      if (page.pages && page.pages.length > 0) {
        const found = findPageByHref(page.pages, href);
        if (found) return found;
      }
    }
    return null;
  };

  const locationPage = data?.datas
    ? findPageByHref(data.datas, "/цугларах-байршил") ||
      findPageByHref(data.datas, "/tsuglarah-bairshil")
    : null;

  // Set page title
  React.useEffect(() => {
    setPageTitle(locationPage, data);
  }, [locationPage, data]);

  // Get map embed from section 9
  const mapSection = locationPage?.sections?.find(
    (s): s is SectionWithData => isSectionWithData(s) && s.sectionId === 9
  );
  const mapData = mapSection?.data;
  const mapEmbedUrl = mapData?.embed || "";

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <div className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        
       {/* Breadcrumb / Back */}
       <div className="mb-6">
          <Link
            to="/forum" 
            className="inline-flex items-center text-gray-600 hover:text-amber-600 transition-colors bg-white px-4 py-2 rounded-lg shadow-sm border border-gray-100 font-medium"
          >
            ← Буцах
          </Link>
        </div>

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 flex flex-col lg:flex-row">
            
            {/* Content Side */}
            <div className="w-full lg:w-1/3 p-6 sm:p-10 flex flex-col justify-center bg-white z-10">
                {locationPage?.title && (
                    <div className="mb-6">
                        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 leading-tight">
                            {locationPage.title}
                        </h1>
                        <div className="h-1.5 w-20 bg-amber-500 rounded-full mb-6"></div>
                        {locationPage.desc && (
                            <p className="text-gray-600 text-lg leading-relaxed">
                                {locationPage.desc}
                            </p>
                        )}
                    </div>
                )}
                
                <div className="bg-amber-50 rounded-xl p-5 border border-amber-100">
                    <h3 className="font-semibold text-amber-900 mb-2">Тайлбар</h3>
                    <p className="text-amber-800 text-sm">
                        Газрын зураг дээрх улаан тэмдэглэгээ нь таны оршин суугаа газрын ойролцоох цугларах цэгүүдийг заана.
                    </p>
                </div>
            </div>

            {/* Map Side */}
            <div className="w-full lg:w-2/3 h-[500px] lg:h-auto lg:min-h-[600px] bg-gray-100 relative">
                {mapEmbedUrl ? (
                    <iframe
                    src={mapEmbedUrl}
                    className="absolute inset-0 w-full h-full"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title={mapData?.title || "Цугларах газрын байршил"}
                    />
                ) : (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400">
                        <svg className="w-16 h-16 mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0121 18.382V7.618a1 1 0 01-.553-.894L15 4m0 13V4m0 0L9 7" />
                        </svg>
                        <p>Газрын зураг ачааллаж байна...</p>
                    </div>
                )}
            </div>
        </div>
      </div>
    </div>
  );
};

export default LocationPage;
